import { cloneDeep } from 'lodash';
import findResult, {
  assignValue, findMatch, findWithCodes, hasKey, removeEmpty,
} from '@/utils/resultsHelper';
import { pruneFieldErrors } from '@/utils/errors';
import fields from '@/constants/fields';
import { states } from '@/constants/lifecycle';
import { findWithUUID } from '@/utils/uuidHelper';
import { cohortCategories } from '../constants';
import cohortProfile from '../profile/state/state';

const pruneCohortErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

export const assignEditCohortProps = (item, markAsDirty = false) => {
  const firstRun = !item.copy || !item.isEditing;
  if (firstRun) {
    assignValue(item.copy, 'queries', cloneDeep(item.queries));
    assignValue(item.copy, 'name', item.name);
    assignValue(item.copy, 'description', item.description);
  }

  assignValue(item, 'validationErrors', []);

  if (!item.saveErrors) {
    assignValue(item, 'saveErrors', []);
  }

  if (!item.saveStatus) {
    assignValue(item, 'saveStatus', states.NONE);
  }

  assignValue(item, 'isEditing', true);

  if (markAsDirty) {
    assignValue(item, 'isDirty', true);
  }
  return firstRun;
};

const cohortAndQuery = (cohort, queryUUID, copy = false) => {
  if (copy) {
    return {
      cohort: cohort.copy,
      query: cohort.copy?.queries ? findWithUUID(cohort.copy.queries, queryUUID) : null,
    };
  }
  return { cohort, query: cohort ? findWithUUID(cohort.queries, queryUUID) : null };
};

export const findQuery = (cohorts, { category, code, queryUUID }, copy = false) => (
  cohortAndQuery(findResult(cohorts, category, code), queryUUID, copy)
);

export const findActiveQuery = (cohorts, { category, code }) => {
  const cohort = findResult(cohorts, category, code);
  return cohortAndQuery(cohort, cohort.activeQueryUUID);
};

export const findQueryClause = (cohorts, {
  category,
  code,
  queryUUID,
  clauseUUID,
}, copy = false) => {
  const { cohort, query } = findQuery(cohorts, { category, code, queryUUID }, copy);
  return { cohort, query, clause: findWithUUID((query?.clauses || []), clauseUUID) };
};

export const findQueryDictionary = (cohorts, {
  category,
  code,
  queryUUID,
  dictionaryCode,
}) => {
  const { cohort, query } = findQuery(cohorts, { category, code, queryUUID });
  const clauses = query?.clauses || [];
  const dictionaries = query?.dataset?.dictionaries || [];

  return {
    cohort,
    query,
    dictionary: dictionaries.find((r) => (r.code === dictionaryCode)),
    clauses: clauses.filter((r) => (r.dictionary && r.dictionary.code === dictionaryCode)),
  };
};

export const findQueryProfile = (cohorts, {
  category,
  code,
  queryUUID,
  dictionaryCode,
}) => {
  const {
    cohort, query, dictionary, clauses,
  } = findQueryDictionary(cohorts, {
    category, code, queryUUID, dictionaryCode,
  });
  const profiles = query?.profiles || [];

  return {
    cohort, query, dictionary, clauses, profile: findMatch(profiles, dictionaryCode) || cohortProfile(),
  };
};

export const findQueryClauseProfile = (cohorts, {
  category,
  code,
  queryUUID,
  clauseUUID,
}) => {
  const { cohort, query, clause } = findQueryClause(cohorts, {
    category, code, queryUUID, clauseUUID,
  });
  return {
    cohort, query, clause, profile: clause.profile,
  };
};

export const findQueryFilter = (cohorts, {
  category,
  code,
  queryUUID,
  clauseUUID,
  filterUUID,
}, copy = false) => {
  const { cohort, query, clause } = findQueryClause(cohorts, {
    category, code, queryUUID, clauseUUID,
  }, copy);
  return {
    cohort, query, clause, filter: findWithUUID((clause?.filters || []), filterUUID),
  };
};

const cohortSettingsAndEntities = (query, cohort, profile, showEntities = true) => ({
  cohort,
  query,
  profile,
  settings: cohort.settings,
  entities: showEntities && cohort.settings.expose_cohort_counts ? profile.entities : [],
});

export const findQueryClauseSettings = (cohorts, {
  category,
  code,
  queryUUID,
  clauseUUID,
}) => {
  const {
    cohort, query, clause, profile,
  } = findQueryClauseProfile(cohorts, {
    category, code, queryUUID, clauseUUID,
  });
  return { clause, ...cohortSettingsAndEntities(query, cohort, profile, profile.totalFiltered > 0) };
};

export const findQuerySettings = (cohorts, {
  category,
  code,
  queryUUID,
  dictionaryCode,
}) => {
  const { cohort, query, profile } = findQueryProfile(cohorts, {
    category, code, queryUUID, dictionaryCode,
  });
  return cohortSettingsAndEntities(query, cohort, profile);
};

export const uniqueDictionariesInClauses = ({ clauses }, includeEmpty = false) => {
  const dictionaries = clauses.map((c) => c.dictionary);
  const prunedDictionaries = removeEmpty(dictionaries);
  const uniqueDictionaries = prunedDictionaries.filter((v, i, a) => i === a.findIndex((t) => (t.code === v.code)));
  const removedDictionary = includeEmpty && dictionaries.length !== prunedDictionaries.length ? [{ }] : [];

  return uniqueDictionaries.concat(removedDictionary);
};

export const findCohorts = ({ cohorts }, { category }) => cohorts[category];

export const findAllCohorts = ({ cohorts }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(cohorts, cohortCategories, codes);
};

export const findCohort = ({ cohorts }, { category, code }, attribute = 'code') => (
  findResult(cohorts, category, code, attribute)
);

export default pruneCohortErrors;
